import { useState, useRef } from "react";
import styles from "./aboutCard.module.css";
import {
  IconBuilding,
  IconCategory,
  IconChevronDown,
  IconCircleCheck,
  IconEdit,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import {
  companySize,
  companyType,
  INDUSTRY_SUGGESTIONS,
} from "../../../../constants";
import { companyInfoType } from "../../../../services/clientCalls";
import { errorHandler } from "../../../../utils";
import { Select } from "../../FormInput/input";
import Button from "../../Button/button";
import { toTitleCase } from "../../../../utils/functions";

/* Industry editor sub-component */
function IndustryEditor({ industries, onChange }) {
  const [inputVal, setInputVal] = useState("");
  const [acOpen, setAcOpen] = useState(false);
  const [acActiveIdx, setAcIdx] = useState(-1);
  const inputRef = useRef(null);

  const filteredSuggestions =
    inputVal.length >= 2
      ? INDUSTRY_SUGGESTIONS.filter(
          (s) =>
            s.toLowerCase().includes(inputVal?.toLowerCase()) &&
            !industries?.includes(s),
        ).slice(0, 7)
      : [];

  const addIndustry = (val) => {
    const v = toTitleCase(val);
    if (!v || industries?.includes(v)) {
      setInputVal("");
      setAcOpen(false);
      return;
    }
    onChange([...industries, v]);
    setInputVal("");
    setAcOpen(false);
    setAcIdx(-1);
    inputRef.current?.focus();
  };

  const removeIndustry = (ind) =>
    onChange(industries?.filter((i) => i !== ind));

  return (
    <div>
      <div className={styles.tagList}>
        {industries?.map((ind) => (
          <span key={ind} className={styles.tagEditable}>
            {ind}
            <button
              className={styles.tagRemove}
              onClick={() => removeIndustry(ind)}
              aria-label={`Remove ${ind}`}
            >
              <IconX size={10} />
            </button>
          </span>
        ))}
      </div>
      <div className={styles.tagAddRow}>
        <div className={styles.autocompleteWrap}>
          <input
            ref={inputRef}
            type="text"
            className={styles.tagInput}
            placeholder="e.g. Fintech, Healthcare…"
            autoComplete="off"
            value={inputVal}
            onChange={(e) => {
              setInputVal(e.target.value);
              setAcOpen(true);
              setAcIdx(-1);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setAcIdx((i) =>
                  Math.min(i + 1, filteredSuggestions.length - 1),
                );
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setAcIdx((i) => Math.max(i - 1, -1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                acActiveIdx >= 0
                  ? addIndustry(filteredSuggestions[acActiveIdx])
                  : addIndustry(inputVal);
              } else if (e.key === "Escape") {
                setAcOpen(false);
                setAcIdx(-1);
              }
            }}
            onBlur={() => setTimeout(() => setAcOpen(false), 150)}
          />
          <div
            className={`${styles.autocompleteList} ${acOpen && filteredSuggestions.length > 0 ? styles.autocompleteListOpen : ""}`}
          >
            {filteredSuggestions.map((s, i) => {
              const q = inputVal.toLowerCase();
              const idx = s.toLowerCase().indexOf(q);
              return (
                <div
                  key={s}
                  className={`${styles.autocompleteItem} ${i === acActiveIdx ? styles.autocompleteItemActive : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addIndustry(s);
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      s.slice(0, idx) +
                      `<mark>${s.slice(idx, idx + q.length)}</mark>` +
                      s.slice(idx + q.length),
                  }}
                />
              );
            })}
          </div>
        </div>
        <button
          className={styles.tagAddBtn}
          onClick={() => addIndustry(inputVal)}
        >
          + Add
        </button>
      </div>
    </div>
  );
}

const ClassificationCard = ({ profileData, getCompanyProfile }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [load, setLoad] = useState(false);

  const handleEdit = () => {
    if (editing) {
      setEditing(false);
      setDraft(null);
    } else {
      setDraft({
        companyType: profileData?.companyType,
        companySize: profileData?.companySize,
        industry: [...profileData?.industry],
      });
      setEditing(true);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoad(true);
      const res = await companyInfoType(draft);
      getCompanyProfile();
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoad(false);
      setEditing(false);
      setDraft(null);
    }
  };

  const current = editing ? draft : profileData;

  return (
    <div className={styles.card} id="classCard">
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconCategory aria-hidden="true" />
          </div>
          <div>
            <div className={styles.cardTitle}>Classification</div>
            <div className={styles.cardSubtitle}>
              Company type, industry &amp; size
            </div>
          </div>
        </div>
        <div
          className={`${styles.btnGroup} ${editing ? styles.btnGroupEditing : ""}`}
        >
          {editing && (
            <Button
              variant="secondary"
              leftIcon={<IconCircleCheck size={14} />}
              size="sm"
              onClick={handleUpdate}
              disabled={load}
              loading={load}
            >
              Update
            </Button>
          )}
          <Button
            leftIcon={editing ? <IconX size={14} /> : <IconEdit size={14} />}
            size="sm"
            onClick={handleEdit}
          >
            {editing ? "Cancel" : "Edit"}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className={styles.cardBody}>
        <div className={styles.fieldsWrap}>
          {/* Company Type */}
          <div className={styles.infoField}>
            {!editing ? (
              <div>
                <p>
                  <span className={styles.infoLabel}>Company Type</span>
                </p>
                <div className={styles.tagList} id="classTypeDisplay">
                  {current.companyType ? (
                    <span className={styles.tag}>
                      <IconBuilding size={14} aria-hidden="true" />
                      {current.companyType}
                    </span>
                  ) : (
                    <span className={styles.infoValueEmpty}>Not specified</span>
                  )}
                </div>
              </div>
            ) : (
              <>
                {draft?.companyType && (
                  <div className={styles.tagList}>
                    <span className={styles.tagEditable}>
                      {draft?.companyType}
                      <button
                        className={styles.tagRemove}
                        onClick={() =>
                          setDraft((draft) => ({ ...draft, companyType: "" }))
                        }
                        aria-label="Remove type"
                      >
                        <IconX size={10} />
                      </button>
                    </span>
                  </div>
                )}
                {!draft?.companyType && (
                  <Select
                    label="Company Type"
                    value=""
                    onChange={(e) => {
                      if (e.target.value)
                        setDraft((draft) => ({
                          ...draft,
                          companyType: e.target.value,
                        }));
                    }}
                  >
                    <option value="" disabled>
                      — Select company type —
                    </option>
                    {companyType?.map((item) => (
                      <option value={item.text}>{item.text}</option>
                    ))}
                  </Select>
                )}
              </>
            )}
          </div>

          {/* Industry */}
          <div className={styles.infoField}>
            <span className={styles.infoLabel}>Industry</span>
            {!editing ? (
              <div className={styles.tagList} id="classIndustryDisplay">
                {current?.industry?.length > 0 ? (
                  current?.industry?.map((ind) => (
                    <span key={ind} className={styles.tag}>
                      {ind}
                    </span>
                  ))
                ) : (
                  <span className={styles.infoValueEmpty}>Not specified</span>
                )}
              </div>
            ) : (
              <IndustryEditor
                industries={draft?.industry}
                onChange={(inds) => setDraft((d) => ({ ...d, industry: inds }))}
              />
            )}
          </div>

          {/* Company Size */}
          <div className={styles.infoField}>
            {!editing ? (
              <div>
                <p>
                  <span className={styles.infoLabel}>Company Size</span>
                </p>
                <div className={styles.tagList} id="classSizeDisplay">
                  {current.companySize ? (
                    <span className={styles.tag}>
                      <IconUsers aria-hidden="true" size={14} />
                      {
                        companySize.find(
                          (item) => item.number === Number(current.companySize),
                        )?.employees
                      }
                    </span>
                  ) : (
                    <span className={styles.infoValueEmpty}>Not specified</span>
                  )}
                </div>
              </div>
            ) : (
              <>
                {draft?.companySize && (
                  <div className={styles.tagList}>
                    <span className={styles.tagEditable}>
                      {
                        companySize.find(
                          (item) => item.number === Number(draft.companySize),
                        )?.employees
                      }
                      <button
                        className={styles.tagRemove}
                        onClick={() =>
                          setDraft((d) => ({ ...d, companySize: "" }))
                        }
                        aria-label="Remove size"
                      >
                        <IconX size={10} />
                      </button>
                    </span>
                  </div>
                )}
                {!draft?.companySize && (
                  <Select
                    label="Company Size"
                    value=""
                    onChange={(e) => {
                      if (e.target.value)
                        setDraft((d) => ({
                          ...d,
                          companySize: Number(e.target.value),
                        }));
                    }}
                  >
                    <option value="">— Select company size —</option>
                    {companySize.map((item) => (
                      <option key={item.number} value={item.number}>
                        {item.employees}
                      </option>
                    ))}
                  </Select>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassificationCard;
